/**
 * Voice settings (ADR-114 in platform): who speaks a tenant's voicemail greetings, set on one screen.
 *
 * An admin saves the ElevenLabs API key and picks a voice for each language a greeting is written in
 * and for a female and a male rep. A person is female or male on their account, and that picks the
 * voice that says their name. core-api asks ElevenLabs about the key and every voice when they are
 * saved, and says who speaks in each case (`speakers`): the console never works that out itself.
 *
 * The key is write-only: the console sends one and never reads one.
 *
 * What the form refuses here follows core-api's `voice_settings.save`, so the usual mistakes are
 * said beside the field and not as a 422. Pure, for `test/voice-settings.test.ts`.
 */

import { languageName } from "./lead-card";
import { VOICEMAIL_LANGUAGES } from "./voicemail";

export const VOICE_GENDERS = ["female", "male"] as const;
export type VoiceGender = (typeof VOICE_GENDERS)[number];

/** The model a tenant starts on: core-api's `VOICE_DEFAULT_MODEL`. */
export const DEFAULT_VOICE_MODEL = "eleven_flash_v2_5";
/** The longest sentence the test speaks: core-api's `voice_settings.TEST_MAX`. */
export const VOICE_TEST_MAX = 300;
/** Longer than any key ElevenLabs has made: core-api's `voice_settings.KEY_MAX`. */
export const VOICE_KEY_MAX = 200;

/** The voice set in one slot. */
export interface VoicePick {
  voice_id: string;
  /** What ElevenLabs called the voice when it was saved. */
  name: string;
}

/** Who speaks a greeting in one language for one kind of rep, as core-api's rule has it. */
export interface VoiceSpeaker {
  language: string;
  /** Null is a rep with nothing set. */
  gender: VoiceGender | null;
  /** Null when no voice is set at all. */
  voice_id: string | null;
  name: string | null;
  /** The slot the voice was set in. They differ from the two above when the voice stands in. */
  from_language: string | null;
  from_gender: VoiceGender | null;
  exact: boolean;
}

export interface VoiceSettings {
  tenant_id: number;
  /** Whether a key is kept. The key itself is never in an answer. */
  api_key_set: boolean;
  model_id: string;
  /** Every slot, by language and then female or male, with the voice set in it or null. */
  voices: Record<string, Record<string, VoicePick | null>>;
  default_gender: VoiceGender | string;
  speakers: VoiceSpeaker[];
  /** When ElevenLabs last accepted the key. */
  checked_at: string | null;
  updated_at: string | null;
  /**
   * Why ElevenLabs could not speak a greeting on a call the last time it could not (ADR-128 in
   * platform), in core-api's sentence, and when. Null once a greeting was spoken after it, or the
   * settings were saved.
   */
  last_problem?: string | null;
  last_problem_at?: string | null;
  /** How many people speak in a voice of their own (ADR-117), made in this ElevenLabs account. */
  own_voices?: number;
}

/** A voice of the tenant's ElevenLabs account, as the pickers list it. */
export interface ElevenLabsVoice {
  voice_id: string;
  name: string;
  category: string | null;
  /** female, male or neutral, as ElevenLabs labels the voice. Null for one with no label. */
  gender: string | null;
  accent: string | null;
  age: string | null;
  description: string | null;
  /** The languages ElevenLabs checked the voice in, as two-letter codes. */
  languages: string[];
  /** A sample of the voice. An address of ElevenLabs' own. */
  preview_url: string | null;
  /** A sample in a language, where ElevenLabs has one. */
  previews: Record<string, string>;
}

export interface ElevenLabsModel {
  model_id: string;
  name: string;
  description: string | null;
  languages: string[];
  /** What a character costs against one of the plainest model, when ElevenLabs says. */
  cost: number | null;
}

export interface VoiceCatalog {
  voices: ElevenLabsVoice[];
  /** Whether the account has more voices than are listed. */
  more_voices: boolean;
  /** Empty for a key that may not read the models. */
  models: ElevenLabsModel[];
}

/** What `PUT /integrations/voice` takes: every setting, each time. The key only when one was typed. */
export interface VoiceSettingsInput {
  api_key?: string;
  model_id: string;
  voices: Record<string, Record<string, string | null>>;
  default_gender: VoiceGender;
  /** Superadmin only. */
  tenant_id?: number;
}

/** What `POST /integrations/voice/test` takes. */
export interface VoiceTestInput {
  text: string;
  language: string;
  /** Null is a rep with nothing set. */
  gender: VoiceGender | null;
  tenant_id?: number;
}

export type VoiceTable = Record<string, Record<VoiceGender, VoicePick | null>>;

/** The form. */
export interface VoiceValues {
  /** Typed now. Empty keeps the key already kept. */
  apiKey: string;
  modelId: string;
  voices: VoiceTable;
  defaultGender: VoiceGender;
}

export function isVoiceGender(value: unknown): value is VoiceGender {
  return (VOICE_GENDERS as readonly unknown[]).includes(value);
}

/** "Female" and "Male"; "Not set" for null. */
export function voiceGenderLabel(gender: VoiceGender | null): string {
  return gender === "female" ? "Female" : gender === "male" ? "Male" : "Not set";
}

/**
 * The choices of a person's own voice, on the user form and on their account. The empty value is
 * "not set", which the API takes as null.
 */
export const PERSON_VOICE_OPTIONS: { value: "" | VoiceGender; label: string; description: string }[] = [
  { value: "", label: "Not set", description: "Your default voice speaks for them." },
  { value: "female", label: "Female voice", description: "The female voice of the lead's language." },
  { value: "male", label: "Male voice", description: "The male voice of the lead's language." },
];

/** The same choices as a person reads them on their own account. */
export const OWN_VOICE_OPTIONS: { value: "" | VoiceGender; label: string; description: string }[] = [
  { value: "", label: "Not set", description: "Your team's default voice speaks for you." },
  { value: "female", label: "Female voice", description: "A female voice says your name." },
  { value: "male", label: "Male voice", description: "A male voice says your name." },
];

/** Every slot there is, with nothing in it. */
function emptyTable(): VoiceTable {
  const table: VoiceTable = {};
  for (const language of VOICEMAIL_LANGUAGES) table[language] = { female: null, male: null };
  return table;
}

/** The form as it is loaded from the settings. The key is never loaded: core-api never sends one. */
export function voiceValues(settings: VoiceSettings): VoiceValues {
  const voices = emptyTable();
  for (const language of VOICEMAIL_LANGUAGES) {
    for (const gender of VOICE_GENDERS) {
      const pick = settings.voices[language]?.[gender];
      if (pick?.voice_id) voices[language]![gender] = { voice_id: pick.voice_id, name: pick.name || pick.voice_id };
    }
  }
  return {
    apiKey: "",
    modelId: settings.model_id || DEFAULT_VOICE_MODEL,
    voices,
    defaultGender: isVoiceGender(settings.default_gender) ? settings.default_gender : "female",
  };
}

/** One slot of the form set to a voice, or cleared. A new object: the form is state. */
export function withVoice(
  values: VoiceValues, language: string, gender: VoiceGender, pick: VoicePick | null,
): VoiceValues {
  const row = values.voices[language] ?? { female: null, male: null };
  return { ...values, voices: { ...values.voices, [language]: { ...row, [gender]: pick } } };
}

function slotIds(voices: VoiceTable): string {
  return VOICEMAIL_LANGUAGES.flatMap((language) =>
    VOICE_GENDERS.map((gender) => voices[language]?.[gender]?.voice_id ?? ""),
  ).join("|");
}

/** Whether saving would change anything: a key typed, another model, default or voice. */
export function voiceChanged(values: VoiceValues, loaded: VoiceValues): boolean {
  return (
    values.apiKey.trim() !== ""
    || values.modelId !== loaded.modelId
    || values.defaultGender !== loaded.defaultGender
    || slotIds(values.voices) !== slotIds(loaded.voices)
  );
}

/** Why the form cannot be saved, in core-api's own words, or null. */
export function voiceProblem(values: VoiceValues, settings: VoiceSettings): string | null {
  const key = values.apiKey.trim();
  if (!key && !settings.api_key_set) return "Enter the ElevenLabs API key.";
  if (key.length > VOICE_KEY_MAX || /\s/.test(key)) {
    return "That is not an API key: a key has no spaces in it. Paste the key alone.";
  }
  return null;
}

/** What is sent. The key only when one was typed: left out, core-api keeps the one it has. */
export function voicePayload(values: VoiceValues): VoiceSettingsInput {
  const voices: VoiceSettingsInput["voices"] = {};
  for (const language of VOICEMAIL_LANGUAGES) {
    voices[language] = {
      female: values.voices[language]?.female?.voice_id ?? null,
      male: values.voices[language]?.male?.voice_id ?? null,
    };
  }
  const key = values.apiKey.trim();
  return {
    ...(key ? { api_key: key } : {}),
    model_id: values.modelId,
    voices,
    default_gender: values.defaultGender,
  };
}

/** Whether the tenant can speak a greeting at all: a key is kept and at least one voice is set.
 *  core-api's `voice_for` finds a voice for every case from then on. */
export function canSpeak(settings: VoiceSettings): boolean {
  return settings.api_key_set && voicesSet(voiceValues(settings).voices) > 0;
}

/** How many slots have a voice. */
export function voicesSet(voices: VoiceTable): number {
  return slotIds(voices).split("|").filter(Boolean).length;
}

// --- the account's voices -------------------------------------------------------------------------

function words(value: string | null): string {
  if (!value) return "";
  const text = value.replace(/[_-]+/g, " ").trim();
  return text ? text[0]!.toUpperCase() + text.slice(1) : "";
}

/** A voice in one line, by how ElevenLabs labels it: "Female · American · Young · English, Spanish". */
export function voiceLine(voice: ElevenLabsVoice): string {
  const languages = voice.languages.map((code) => languageName(code) ?? code);
  const listed = languages.length > 3
    ? `${languages.slice(0, 3).join(", ")} +${languages.length - 3}`
    : languages.join(", ");
  return [words(voice.gender), words(voice.accent), words(voice.age), listed].filter(Boolean).join(" · ");
}

/** The sample to play for a slot: the one in the slot's language where ElevenLabs has one. */
export function voiceSample(voice: ElevenLabsVoice, language: string): string | null {
  return voice.previews[language] ?? voice.preview_url ?? null;
}

/** How well a voice fits a language: made in it, checked in it, or neither. Lower is better. */
function languageRank(voice: ElevenLabsVoice, language: string): number {
  if (voice.languages[0] === language) return 0;
  return voice.languages.includes(language) ? 1 : 2;
}

/**
 * The account's voices for one slot's chooser: the ones ElevenLabs labels with the slot's kind first,
 * and every other voice after them. In each list a voice made in the slot's language comes before
 * one only checked in it, and that before the rest. A voice with no label, or the other kind's, can
 * still be picked: the label is ElevenLabs' and it is often missing on a voice an account made itself.
 *
 * `search` narrows both lists by every word typed, each the start of a word in the name, the labels
 * or the description: "male" finds the male voices and not the female ones.
 */
export function chooserVoices(
  voices: ElevenLabsVoice[], language: string, gender: VoiceGender, search = "",
): { fitting: ElevenLabsVoice[]; others: ElevenLabsVoice[] } {
  const typed = search.toLowerCase().split(/\s+/).filter(Boolean);
  const matches = (voice: ElevenLabsVoice) => {
    const said = `${voice.name} ${voiceLine(voice)} ${voice.description ?? ""}`
      .toLowerCase().split(/[^\p{L}\p{N}]+/u).filter(Boolean);
    return typed.every((word) => said.some((part) => part.startsWith(word)));
  };
  const byName = (a: ElevenLabsVoice, b: ElevenLabsVoice) =>
    languageRank(a, language) - languageRank(b, language) || a.name.localeCompare(b.name);
  const shown = voices.filter(matches);
  return {
    fitting: shown.filter((voice) => voice.gender === gender).sort(byName),
    others: shown.filter((voice) => voice.gender !== gender).sort(byName),
  };
}

/**
 * The slots whose voice the account no longer lists, as "English female". Empty while the account
 * has more voices than the catalog read: a voice that is not listed may then just be further down.
 */
export function goneVoices(voices: VoiceTable, catalog: VoiceCatalog | undefined): string[] {
  if (!catalog || catalog.more_voices) return [];
  const known = new Set(catalog.voices.map((voice) => voice.voice_id));
  const gone: string[] = [];
  for (const language of VOICEMAIL_LANGUAGES) {
    for (const gender of VOICE_GENDERS) {
      const pick = voices[language]?.[gender];
      if (pick && !known.has(pick.voice_id)) gone.push(slotName(language, gender));
    }
  }
  return gone;
}

/** A slot in words: "Spanish female". */
export function slotName(language: string, gender: VoiceGender): string {
  return `${languageName(language) ?? language} ${gender}`;
}

// --- the model ------------------------------------------------------------------------------------

/**
 * The models to choose from: the account's, and the saved one first when the account does not list
 * it (a key that may not read the models lists none), so the select always shows what is saved.
 */
export function modelChoices(models: ElevenLabsModel[], current: string): ElevenLabsModel[] {
  if (models.some((model) => model.model_id === current)) return models;
  return [{ model_id: current, name: current, description: null, languages: [], cost: null }, ...models];
}

/** What a model costs, in words. Empty when ElevenLabs does not say. */
export function modelCost(cost: number | null): string {
  if (cost === null || !(cost > 0)) return "";
  if (cost === 1) return "The standard price a character.";
  if (cost === 0.5) return "Half the standard price a character.";
  return `${Number(cost.toFixed(2))} times the standard price a character.`;
}

/** The languages a greeting is written in that a model does not list. Empty when it lists none at all. */
export function modelLacks(model: ElevenLabsModel | undefined): string[] {
  if (!model || model.languages.length === 0) return [];
  return VOICEMAIL_LANGUAGES.filter((code) => !model.languages.includes(code))
    .map((code) => languageName(code) ?? code);
}

// --- who speaks, and the test ---------------------------------------------------------------------

/** The saved settings' answer for one case. */
export function speakerFor(
  settings: VoiceSettings, language: string, gender: VoiceGender | null,
): VoiceSpeaker | undefined {
  return settings.speakers.find((speaker) => speaker.language === language && speaker.gender === gender);
}

/**
 * Who speaks, as the table says it: the voice, and for one that stands in, the slot it was set in.
 * `standIn` is null for the voice set for the case itself, and for nobody.
 */
export function speakerText(speaker: VoiceSpeaker | undefined): { name: string; standIn: string | null } {
  if (!speaker?.voice_id) return { name: "Nobody", standIn: null };
  const name = speaker.name || speaker.voice_id;
  if (speaker.exact || !speaker.from_language || !speaker.from_gender) return { name, standIn: null };
  return { name, standIn: `The ${slotName(speaker.from_language, speaker.from_gender)} voice.` };
}

/** A rep of a kind, in words: "a female rep", "a rep with nothing set". */
export function repName(gender: VoiceGender | null): string {
  return gender ? `a ${gender} rep` : "a rep with nothing set";
}

/** The sentence the test starts with, in each language. Nothing in it is anybody's. */
export const VOICE_TEST_SENTENCES: Record<string, string> = {
  en: "Hi, this is a test of the voice that leaves your voicemail greetings. Please call us back when you can.",
  es: "Hola, esta es una prueba de la voz que deja sus mensajes de voz. Por favor, devuélvanos la llamada cuando pueda.",
};

/** Why a sentence cannot be spoken, in core-api's own words, or null. */
export function testProblem(text: string): string | null {
  const sentence = text.trim().replace(/\s+/g, " ");
  if (!sentence) return "Type a sentence to hear.";
  if (sentence.length > VOICE_TEST_MAX) return `The test speaks at most ${VOICE_TEST_MAX} characters.`;
  return null;
}

/**
 * What the Voices page warns an admin with while ElevenLabs cannot speak the greetings (ADR-128
 * in platform), or null when the last one was spoken: core-api's own sentence, then what the calls
 * do meanwhile. Nobody is on a call the dialer leaves a message on by itself, so this page is
 * where it is said.
 */
export function greetingWarning(settings: Pick<VoiceSettings, "last_problem">): string | null {
  const problem = (settings.last_problem ?? "").trim();
  if (!problem) return null;
  return `${/[.!?]$/.test(problem) ? problem : `${problem}.`} Until a greeting is spoken again, calls leave a recording from the voicemail library where it has one for the lead. This note goes away when a greeting is spoken, or when you save these settings.`;
}

/**
 * What the Voices page says about the voices people recorded themselves (ADR-117), or null when
 * nobody has one: how many there are, and that the table above does not apply to those people.
 */
export function ownVoicesText(settings: Pick<VoiceSettings, "own_voices">): string | null {
  const count = settings.own_voices ?? 0;
  if (count <= 0) return null;
  return count === 1
    ? "1 person speaks in a voice of their own, made from a recording of theirs. Their greetings use it in every language, and the table above is for everybody else."
    : `${count} people speak in voices of their own, made from recordings of theirs. Their greetings use them in every language, and the table above is for everybody else.`;
}
